import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  MessageSquare,
  Building2,
} from 'lucide-react';

const CONTACT = {
  email: 'info@tbkmeet.com',
  supportEmail: 'destek@tbkmeet.com',
  phone: '0 312 922 0635',
  phoneHref: 'tel:+903129220635',
  location: 'Ankara, Türkiye',
  hours: 'Hafta içi 09:00 – 18:00',
};

const contactCards = [
  {
    icon: Mail,
    title: 'E-posta',
    description: 'Genel sorular ve iş birliği',
    value: CONTACT.email,
    href: `mailto:${CONTACT.email}`,
  },
  {
    icon: MessageSquare,
    title: 'Destek',
    description: 'Teknik destek ve hesap işlemleri',
    value: CONTACT.supportEmail,
    href: `mailto:${CONTACT.supportEmail}`,
  },
  {
    icon: Phone,
    title: 'Telefon',
    description: 'Hafta içi ulaşabilirsiniz',
    value: CONTACT.phone,
    href: CONTACT.phoneHref,
  },
  {
    icon: MapPin,
    title: 'Adres',
    description: 'Merkez ofis',
    value: CONTACT.location,
    href: null,
  },
];

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const body = [
      `Ad Soyad: ${formData.name}`,
      `E-posta: ${formData.email}`,
      '',
      formData.message,
    ].join('\n');
    const mailto = `mailto:${CONTACT.email}?subject=${encodeURIComponent(
      formData.subject || 'TBKMeet İletişim'
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-24 pb-20">
      <section className="px-6 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-96 h-96 bg-purple-500/20 rounded-full blur-3xl -top-48 -left-48" />
          <div className="absolute w-96 h-96 bg-pink-500/20 rounded-full blur-3xl -bottom-48 -right-48" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">İletişim</h1>
          <p className="text-xl text-gray-400">
            Sorularınız, destek talepleriniz veya iş birliği için bize ulaşın.
          </p>
        </div>
      </section>

      <section className="px-6 pb-12">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {contactCards.map((card) => {
            const content = (
              <>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center mb-4">
                  <card.icon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-white mb-1">{card.title}</h2>
                <p className="text-sm text-gray-500 mb-3">{card.description}</p>
                <p className="text-purple-300 font-medium break-all">{card.value}</p>
              </>
            );
            const className =
              'bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-5 hover:bg-white/10 transition block h-full';
            return card.href ? (
              <a key={card.title} href={card.href} className={className}>
                {content}
              </a>
            ) : (
              <div key={card.title} className={className}>
                {content}
              </div>
            );
          })}
        </div>
      </section>

      <section className="px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-white mb-2">Mesaj Gönderin</h2>
            <p className="text-gray-400 text-sm mb-6">
              Formu doldurduğunuzda e-posta uygulamanız açılır; yanıtı kısa sürede göndeririz.
            </p>

            {submitted ? (
              <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-5 text-green-200">
                E-posta uygulamanız açıldıysa mesajınızı gönderebilirsiniz. Teşekkürler!
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="mt-4 block text-sm text-purple-300 hover:text-white transition"
                >
                  Yeni mesaj yaz
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-300 mb-1.5">Ad Soyad</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/80 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                      placeholder="Adınız Soyadınız"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1.5">E-posta</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/80 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                      placeholder="ornek@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1.5">Konu</label>
                  <input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/80 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    placeholder="Nasıl yardımcı olabiliriz?"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1.5">Mesaj</label>
                  <textarea
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/80 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 resize-y min-h-[120px]"
                    placeholder="Mesajınızı yazın..."
                  />
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg hover:shadow-purple-500/30 transition"
                >
                  <Send className="w-4 h-4" />
                  Gönder
                </button>
              </form>
            )}
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-600/30 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-purple-300" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Çalışma saatleri</h3>
                  <p className="text-gray-400 text-sm">{CONTACT.hours}</p>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Acil teknik talepler için e-posta hattımız 7/24 açıktır; mesai dışında yanıt süresi uzayabilir.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-600/30 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-purple-300" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Kurumsal satış</h3>
                  <p className="text-gray-400 text-sm">Özel plan ve SLA talepleri</p>
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Kurumsal ihtiyaçlarınız için ekibimizle konuşun; fiyatlandırma sayfasından da başlayabilirsiniz.
              </p>
              <Link
                to="/pricing"
                className="text-sm text-purple-300 hover:text-white transition font-medium"
              >
                Fiyatlandırmaya git →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
